import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Building2, Users, TrendingUp, MapPin, BookOpen,
  Loader2, Download, Eye, Plus, BarChart3, PieChart
} from 'lucide-react';

export default function AdminPartnerPage({ embedded = false }) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerReport, setPartnerReport] = useState(null);
  const [newPartner, setNewPartner] = useState({
    name: '',
    partner_type: 'sponsor',
    contact_name: '',
    contact_email: '',
    locations: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [partnersRes, locationsRes] = await Promise.all([
        api.get('/partner/partners'),
        api.get('/locations/')
      ]);
      // Handle both paginated and non-paginated responses
      setPartners(partnersRes.data?.results || partnersRes.data || []);
      setLocations(locationsRes.data?.results || locationsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      // Demo data
      setPartners([
        { id: 'p1', name: 'TechCorp Foundation', partner_type: 'sponsor', contact_name: 'Jane Smith', contact_email: 'jane@techcorp.com', locations: [], active: true, created_at: new Date().toISOString() },
        { id: 'p2', name: 'Lincoln Elementary', partner_type: 'school', contact_name: 'Principal Johnson', contact_email: 'principal@lincoln.edu', locations: ['loc1'], active: true, created_at: new Date().toISOString() }
      ]);
      setLocations([
        { id: 'loc1', name: 'APS Tower' },
        { id: 'loc2', name: 'North Bay Campus' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const createPartner = async () => {
    if (!newPartner.name || !newPartner.contact_email) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      await api.post('/partner/partners', newPartner);
      toast.success('Partner created successfully!');
      setShowCreateDialog(false);
      setNewPartner({ name: '', partner_type: 'sponsor', contact_name: '', contact_email: '', locations: [] });
      fetchData();
    } catch (error) {
      toast.error('Failed to create partner');
    }
  };

  const loadPartnerReport = async (partnerId) => {
    try {
      const response = await api.get('/partner/dashboard');
      setPartnerReport(response.data);
    } catch (error) {
      console.error('Failed to load report:', error);
      // Demo data
      setPartnerReport({
        metrics: {
          total_students: 156,
          active_enrollments: 89,
          classes_held: 24,
          attendance_rate: 87.5,
          courses_offered: 8
        }
      });
    }
  };

  const partnerTypeColors = {
    sponsor: 'bg-blue-100 text-blue-800',
    school: 'bg-green-100 text-green-800',
    community: 'bg-purple-100 text-purple-800',
    corporate: 'bg-orange-100 text-orange-800'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-partner-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2">Partner Portal</h1>
          <p className="text-muted-foreground">Manage partners and view reporting metrics</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="add-partner-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Partner
        </Button>
      </div>

      <Tabs defaultValue="partners" className="space-y-6">
        <TabsList>
          <TabsTrigger value="partners">Partners</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>

        {/* Partners Tab */}
        <TabsContent value="partners">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {partners.map(partner => (
              <Card key={partner.id} className="card-dashboard">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium">{partner.name}</h3>
                        <Badge className={partnerTypeColors[partner.partner_type]}>
                          {partner.partner_type}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-muted-foreground">
                      <strong>Contact:</strong> {partner.contact_name}
                    </p>
                    <p className="text-muted-foreground">
                      <strong>Email:</strong> {partner.contact_email}
                    </p>
                    <p className="text-muted-foreground">
                      <strong>Locations:</strong> {partner.locations?.length || 0} assigned
                    </p>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setSelectedPartner(partner);
                        loadPartnerReport(partner.id);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View Report
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {partners.length === 0 && (
              <div className="col-span-full text-center py-12">
                <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No partners yet</p>
                <Button variant="outline" className="mt-4" onClick={() => setShowCreateDialog(true)}>
                  Add First Partner
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports">
          <div className="space-y-6">
            {/* Overview Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="card-dashboard">
                <CardContent className="p-4 text-center">
                  <Users className="h-8 w-8 mx-auto mb-2 text-primary" />
                  <p className="text-3xl font-bold">{partnerReport?.metrics?.total_students || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Students</p>
                </CardContent>
              </Card>
              <Card className="card-dashboard">
                <CardContent className="p-4 text-center">
                  <BookOpen className="h-8 w-8 mx-auto mb-2 text-secondary" />
                  <p className="text-3xl font-bold">{partnerReport?.metrics?.active_enrollments || 0}</p>
                  <p className="text-sm text-muted-foreground">Active Enrollments</p>
                </CardContent>
              </Card>
              <Card className="card-dashboard">
                <CardContent className="p-4 text-center">
                  <TrendingUp className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  <p className="text-3xl font-bold">{partnerReport?.metrics?.attendance_rate || 0}%</p>
                  <p className="text-sm text-muted-foreground">Attendance Rate</p>
                </CardContent>
              </Card>
              <Card className="card-dashboard">
                <CardContent className="p-4 text-center">
                  <MapPin className="h-8 w-8 mx-auto mb-2 text-orange-500" />
                  <p className="text-3xl font-bold">{partnerReport?.metrics?.classes_held || 0}</p>
                  <p className="text-sm text-muted-foreground">Classes Held</p>
                </CardContent>
              </Card>
            </div>

            {/* Export Options */}
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle>Export Reports</CardTitle>
                <CardDescription>Download detailed reports for partners</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Button variant="outline" onClick={() => toast.success('Overview report downloaded')}>
                    <Download className="h-4 w-4 mr-2" />
                    Overview
                  </Button>
                  <Button variant="outline" onClick={() => toast.success('Location report downloaded')}>
                    <Download className="h-4 w-4 mr-2" />
                    By Location
                  </Button>
                  <Button variant="outline" onClick={() => toast.success('Course report downloaded')}>
                    <Download className="h-4 w-4 mr-2" />
                    By Course
                  </Button>
                  <Button variant="outline" onClick={() => toast.success('Demographics report downloaded')}>
                    <Download className="h-4 w-4 mr-2" />
                    Demographics
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Partner Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Partner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Partner Name *</Label>
              <Input
                value={newPartner.name}
                onChange={(e) => setNewPartner(p => ({...p, name: e.target.value}))}
                placeholder="Organization name"
                data-testid="partner-name"
              />
            </div>
            <div>
              <Label>Partner Type</Label>
              <Select value={newPartner.partner_type} onValueChange={(v) => setNewPartner(p => ({...p, partner_type: v}))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sponsor">Sponsor</SelectItem>
                  <SelectItem value="school">School</SelectItem>
                  <SelectItem value="community">Community</SelectItem>
                  <SelectItem value="corporate">Corporate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contact Name</Label>
              <Input
                value={newPartner.contact_name}
                onChange={(e) => setNewPartner(p => ({...p, contact_name: e.target.value}))}
                placeholder="Primary contact"
              />
            </div>
            <div>
              <Label>Contact Email *</Label>
              <Input
                type="email"
                value={newPartner.contact_email}
                onChange={(e) => setNewPartner(p => ({...p, contact_email: e.target.value}))}
                placeholder="contact@partner.com"
                data-testid="partner-email"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createPartner} data-testid="create-partner-btn">Create Partner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partner Report Dialog */}
      <Dialog open={!!selectedPartner} onOpenChange={() => setSelectedPartner(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedPartner?.name} - Report</DialogTitle>
          </DialogHeader>
          {partnerReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold">{partnerReport.metrics?.total_students}</p>
                    <p className="text-sm text-muted-foreground">Students</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold">{partnerReport.metrics?.attendance_rate}%</p>
                    <p className="text-sm text-muted-foreground">Attendance</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedPartner(null)}>Close</Button>
            <Button onClick={() => toast.success('Report exported')}>
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
